import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, ShoppingCart, Loader2, FileText, Tag, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Featured ebook cover
const FEATURED_COVER = "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png";

const EbookStorePage = () => {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [purchasingBundle, setPurchasingBundle] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEbooks = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/ebooks`);
        setEbooks(response.data);
      } catch (error) {
        console.error("Error fetching ebooks:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEbooks();
  }, []);

  const handlePurchase = async (ebook) => {
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/login", { state: { from: "/ebooks" } });
      return;
    }

    setPurchasing(ebook.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          ebook_id: ebook.id,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process purchase. Please try again.");
    } finally {
      setPurchasing(null);
    }
  };

  const getBundlePrice = () => {
    return ebooks.reduce((sum, ebook) => sum + ebook.price, 0);
  };

  const getBundleDiscount = () => {
    const total = getBundlePrice();
    return (total * 0.25).toFixed(2); // 25% discount
  };

  const handleBundlePurchase = async () => {
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/login", { state: { from: "/ebooks" } });
      return;
    }

    setPurchasingBundle(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          bundle: true,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process purchase. Please try again.");
    } finally {
      setPurchasingBundle(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  const featuredEbook = ebooks.find(e => e.id === "ebook-1");
  const otherEbooks = ebooks.filter(e => e.id !== "ebook-1");

  return (
    <div data-testid="ebook-store-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Digital Products</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ebook Library
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Practical guides and workbooks to help you start, grow, and manage your business. Instant download, lifetime access.
          </p>
        </div>
      </section>

      {/* Bundle Offer Banner */}
      <section className="bg-[#9D9045] py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-white text-center">
          <span className="text-lg font-medium">
            Bundle All 6 Ebooks & Save 25%! 
          </span>
          <span className="bg-white/20 px-4 py-1 rounded-full text-sm">
            ${(getBundlePrice() * 0.75).toFixed(2)} instead of ${getBundlePrice().toFixed(2)}
          </span>
        </div>
      </section>

      {/* Featured Ebook */}
      {featuredEbook && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img 
                  src={FEATURED_COVER}
                  alt={featuredEbook.title}
                  className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
                />
                <div className="absolute -top-4 -left-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm animate-pulse">
                  LIMITED TIME SALE!
                </div>
              </div>
              <div className="space-y-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#1E3A5F]/10 text-[#1E3A5F] rounded-full text-sm font-medium">
                  <Tag className="h-4 w-4" />
                  {featuredEbook.category}
                </span>
                <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {featuredEbook.title}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {featuredEbook.description}
                </p>
                <div className="flex items-center gap-4">
                  {featuredEbook.on_sale && featuredEbook.original_price && (
                    <span className="text-xl text-gray-400 line-through">${featuredEbook.original_price.toFixed(2)}</span>
                  )}
                  <span className="text-3xl font-bold text-red-600">${featuredEbook.price.toFixed(2)}</span>
                  {featuredEbook.on_sale && (
                    <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-sm font-medium">Save 50%!</span>
                  )}
                  <span className="text-gray-500 flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    {featuredEbook.page_count} pages
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={() => handlePurchase(featuredEbook)}
                    disabled={purchasing === featuredEbook.id}
                    className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6"
                    data-testid="featured-purchase-btn"
                  >
                    {purchasing === featuredEbook.id ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        Buy Now
                      </>
                    )}
                  </Button>
                  <Link to="/ebook">
                    <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] px-8 py-6">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Ebooks Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            More Business Guides
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherEbooks.map((ebook) => (
              <div 
                key={ebook.id}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 group"
                data-testid={`ebook-card-${ebook.id}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img 
                    src={ebook.image_url}
                    alt={ebook.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="inline-block px-3 py-1 bg-[#9D9045] text-white text-xs font-medium rounded-full">
                      {ebook.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {ebook.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {ebook.description}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-[#1E3A5F]">${ebook.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-500">{ebook.page_count} pages</span>
                  </div>
                  <Button 
                    onClick={() => handlePurchase(ebook)}
                    disabled={purchasing === ebook.id}
                    className="w-full bg-[#1E3A5F] hover:bg-[#162c47] text-white"
                    data-testid={`purchase-${ebook.id}`}
                  >
                    {purchasing === ebook.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Buy Now
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle Section */}
      <section className="py-16 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Get All 6 Ebooks - Save 25%
          </h2>
          <p className="text-gray-300 mb-6">
            The complete OMW Business Library: Money management, business credit, LLC formation, and business planning.
          </p>
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-gray-400 line-through text-xl">${getBundlePrice().toFixed(2)}</span>
            <span className="text-4xl font-bold text-[#9D9045]">${(getBundlePrice() * 0.75).toFixed(2)}</span>
          </div>
          <Button 
            className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg"
            onClick={handleBundlePurchase}
            disabled={purchasingBundle}
            data-testid="bundle-purchase-btn"
          >
            {purchasingBundle ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ShoppingCart className="mr-2 h-5 w-5" />
                Get the Bundle
              </>
            )}
          </Button>
          <p className="text-gray-400 text-sm mt-4">
            Instant download • Lifetime access • 100% satisfaction guaranteed
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Want Free Resources First?
          </h2>
          <p className="text-gray-600 mb-6">
            Download our free business checklists before you buy.
          </p>
          <Link to="/free-resources">
            <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] px-8">
              Get Free Checklists
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EbookStorePage;
